import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// Determine the API base URL dynamically
const getBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Otherwise, derive it from the current host
  const { protocol, hostname, port } = window.location;

  // If we're running on localhost with a specific port for frontend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // For local development, the backend is typically running on port 8020
    return 'http://localhost:8020';
  }

  // For production, use the same origin since the FastAPI backend serves the frontend
  // The backend handles API routes with the /api prefix
  return window.location.origin;
};

// Create an axios instance with default config
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Clear token from localStorage
      localStorage.removeItem('token');

      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API functions for authentication
export const authService = {
  // Login user
  login: async (username, password) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await api.post('/api/auth/token', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Register user
  register: async (username, email, password) => {
    try {
      const response = await api.post('/api/auth/register', {
        username,
        email,
        password
      });
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  },
};

// API functions for clinics
export const clinicService = {
  // Get all clinics with pagination and search
  getClinics: async (page = 1, search = '', status = 'all') => {
    try {
      const response = await api.get(`/api/clinics`, {
        params: {
          page,
          search: search || undefined,
          status: status !== 'all' ? status : undefined
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching clinics:', error);
      throw error;
    }
  },

  // Create a new clinic
  createClinic: async (clinicData) => {
    try {
      const response = await api.post('/api/clinics', clinicData);
      return response.data;
    } catch (error) {
      console.error('Error creating clinic:', error);
      throw error;
    }
  },

  // Get clinic details
  getClinicDetails: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Alias for getClinicDetails for backward compatibility
  getClinicById: async (clinicId) => {
    return clinicService.getClinicDetails(clinicId);
  },

  // Update clinic configuration
  updateClinic: async (clinicId, clinicData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}`, clinicData);
      return response.data;
    } catch (error) {
      console.error(`Error updating clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update clinic status
  updateClinicStatus: async (clinicId, status) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating clinic ${clinicId} status:`, error);
      throw error;
    }
  },

  // Update clinic ID
  updateClinicId: async (clinicId, newId) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/update-id`, { new_id: newId });
      return response.data;
    } catch (error) {
      console.error(`Error updating clinic ${clinicId} ID:`, error);
      throw error;
    }
  },

  // Toggle clinic status (for backward compatibility)
  toggleClinicStatus: async (clinicId) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/toggle`);
      return response.data;
    } catch (error) {
      console.error(`Error toggling clinic ${clinicId} status:`, error);
      throw error;
    }
  },

  // Get clinic documents
  getClinicDocuments: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/documents`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching documents for clinic ${clinicId}:`, error);
      // Return empty documents array on error to prevent UI crashes
      return { documents: [] };
    }
  },

  // Add a document to a clinic
  addClinicDocument: async (clinicId, documentData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/documents`, documentData);
      return response.data;
    } catch (error) {
      console.error(`Error adding document to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update a clinic document
  updateClinicDocument: async (clinicId, documentId, documentData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/documents/${documentId}`, documentData);
      return response.data;
    } catch (error) {
      console.error(`Error updating document ${documentId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a clinic document
  deleteClinicDocument: async (clinicId, documentId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting document ${documentId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get conversations for a clinic
  getClinicConversations: async (clinicId, page = 1, perPage = 5, search = '') => {
    try {
      const response = await api.get(`/api/conversations/clinic/${clinicId}`, {
        params: { page, per_page: perPage, search: search || undefined },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching conversations for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get conversation details
  getConversationDetails: async (clinicId, conversationId) => {
    try {
      const response = await api.get(`/api/conversations/clinic/${clinicId}/detail/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Alias for getConversationDetails for backward compatibility
  getConversationDetail: async (clinicId, conversationId) => {
    return clinicService.getConversationDetails(clinicId, conversationId);
  },

  // Get messages for a conversation
  getConversationMessages: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/messages`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching messages for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Get earlier messages for a conversation before a specific message ID
  getConversationMessagesBefore: async (conversationId, cursor) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/messages`, {
        params: { before_id: cursor, limit: 60 }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching earlier messages for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Check if the documents table exists
  checkDocumentsTable: async () => {
    try {
      const response = await api.get(`/api/clinics/check-documents-table`);
      return response.data;
    } catch (error) {
      console.error('Error checking documents table:', error);
      return { exists: false, error: error.message };
    }
  },

  // Get clinic subscriptions
  getClinicSubscriptions: async (clinicId, tag = null, excludeTag = null) => {
    try {
      const params = {};
      if (tag) params.tag = tag;
      if (excludeTag) params.exclude_tag = excludeTag;

      const response = await api.get(`/api/clinics/${clinicId}/subscriptions`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching subscriptions for clinic ${clinicId}:`, error);
      // Return empty subscriptions array on error to prevent UI crashes
      return { subscriptions: [] };
    }
  },

  // Get subscription tag options for a clinic
  getSubscriptionTagOptions: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/subscriptions/tag-options`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tag options for clinic ${clinicId}:`, error);
      // Return default tag options on error
      return {
        tagOptions: [
          { value: "emergency", label: "Emergency" },
          { value: "testing", label: "Testing" }
        ]
      };
    }
  },

  // Add a subscription to a clinic
  addClinicSubscription: async (clinicId, subscriptionData) => {
    try {
      // Ensure tags property is an array
      const data = {
        ...subscriptionData,
        tags: Array.isArray(subscriptionData.tags) ? subscriptionData.tags : []
      };
      const response = await api.post(`/api/clinics/${clinicId}/subscriptions`, data);
      return response.data;
    } catch (error) {
      console.error(`Error adding subscription to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update a clinic subscription
  updateClinicSubscription: async (clinicId, subscriptionId, subscriptionData) => {
    try {
      // Ensure tags property is an array
      const data = {
        ...subscriptionData,
        tags: Array.isArray(subscriptionData.tags) ? subscriptionData.tags : []
      };
      const response = await api.put(`/api/clinics/${clinicId}/subscriptions/${subscriptionId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating subscription ${subscriptionId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a clinic subscription
  deleteClinicSubscription: async (clinicId, subscriptionId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/subscriptions/${subscriptionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting subscription ${subscriptionId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  checkVoiceAsset: async (clinicId, assetType) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/asset/${assetType}`);
      return response.data;
    } catch (error) {
      console.error(`Error checking voice asset for clinic ${clinicId}:`, error);
      return {
        exists: false,
        error: error.message
      };
    }
  },

  uploadVoiceAsset: async (clinicId, assetType, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/clinics/${clinicId}/asset/${assetType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading voice asset for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Check if greeting voice asset exists
  checkGreetingVoiceAsset: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/asset/greeting_voice`);
      return response.data;
    } catch (error) {
      console.error(`Error checking greeting voice asset for clinic ${clinicId}:`, error);
      return {
        exists: false,
        error: error.message
      };
    }
  },

  // Upload greeting voice asset
  uploadGreetingVoiceAsset: async (clinicId, file) => {
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/clinics/${clinicId}/asset/greeting_voice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading greeting voice asset for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  checkVoiceMailGreetingAsset: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/asset/voice_mail_voice`);
      return response.data;
    } catch (error) {
      console.error(`Error checking voice mail greeting asset for clinic ${clinicId}:`, error);
      return {
        exists: false,
        error: error.message
      };
    }
  },

  uploadVoiceMailGreetingAsset: async (clinicId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/clinics/${clinicId}/asset/voice_mail_voice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading voice mail greeting asset for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Extract documents for a clinic
  extractDocuments: async (clinicId) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/extract-documents`);
      return response.data;
    } catch (error) {
      console.error(`Error extracting documents for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Notify clinic by human through conversation
  notifyClinicByHuman: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/notify_clinic_by_human`, { message_body: message });
      return response.data;
    } catch (error) {
      console.error(`Error notifying clinic for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Get appointments for a conversation
  getConversationAppointments: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/appointments`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointments for conversation ${conversationId}:`, error);
      // Return empty appointments array on error to prevent UI crashes
      return { appointments: [] };
    }
  },

  // Get conversation events
  getConversationEvents: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/events`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation events:', error);
      throw error;
    }
  },

  // Get block configuration for a clinic
  getClinicBlockConfig: async (clinicId, futureOnly = false) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/block-config`, {
        params: { future_only: futureOnly }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching block config for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get doctors for a clinic
  getClinicDoctors: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/doctors`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching doctors for clinic ${clinicId}:`, error);
      // Return empty doctors array on error to prevent UI crashes
      return { doctors: [], total: 0 };
    }
  },

  // Add a doctor to a clinic
  addClinicDoctor: async (clinicId, doctorData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/doctors`, doctorData);
      return response.data;
    } catch (error) {
      console.error(`Error adding doctor to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update a clinic doctor
  updateClinicDoctor: async (clinicId, doctorId, doctorData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/doctors/${doctorId}`, doctorData);
      return response.data;
    } catch (error) {
      console.error(`Error updating doctor ${doctorId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a clinic doctor
  deleteClinicDoctor: async (clinicId, doctorId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/doctors/${doctorId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting doctor ${doctorId} from clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Apply 2025 holidays to clinic block configuration
  applyHolidays: async (clinicId, doctorId = null) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/block-config/apply-holidays`, {
        doctor_id: doctorId
      });
      return response.data;
    } catch (error) {
      console.error(`Error applying holidays to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Add a new block to clinic configuration
  addClinicBlock: async (clinicId, blockData) => {
    try {
      const timezoneOffset = new Date().getTimezoneOffset() / 60;
      if (blockData.block_from) {
        blockData.block_from = dayjs(blockData.block_from).subtract(timezoneOffset, 'hours').utc().format();
      }
      if (blockData.block_until) {
        blockData.block_until = dayjs(blockData.block_until).subtract(timezoneOffset, 'hours').utc().format();
      }
      // Ensure dates are in UTC format without changing the time
      const sanitizedBlockData = {
        ...blockData,
      };
      const response = await api.post(`/api/clinics/${clinicId}/block-config/blocks`, sanitizedBlockData);
      return response.data;
    } catch (error) {
      console.error(`Error adding block to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an existing block in clinic configuration
  updateClinicBlock: async (clinicId, blockId, blockData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/block-config/blocks/${blockId}`, blockData);
      return response.data;
    } catch (error) {
      console.error(`Error updating block ${blockId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a block from clinic configuration
  deleteClinicBlock: async (clinicId, blockId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/block-config/blocks/${blockId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting block ${blockId} from clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get a specific block from clinic configuration
  getClinicBlock: async (clinicId, blockId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/block-config/blocks/${blockId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching block ${blockId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get special notices for a clinic
  getClinicSpecialNotices: async (clinicId, futureOnly = false) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/special-notices`, {
        params: { future_only: futureOnly }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching special notices for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Add a new special notice
  addClinicSpecialNotice: async (clinicId, noticeData) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/special-notices`, noticeData);
      return response.data;
    } catch (error) {
      console.error(`Error adding special notice to clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an existing special notice
  updateClinicSpecialNotice: async (clinicId, noticeId, noticeData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/special-notices/${noticeId}`, noticeData);
      return response.data;
    } catch (error) {
      console.error(`Error updating special notice ${noticeId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete a special notice
  deleteClinicSpecialNotice: async (clinicId, noticeId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/special-notices/${noticeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting special notice ${noticeId} from clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // QA Knowledge Base
  getClinicQAItems: async (clinicId, skip = 0, limit = 100, isActive = null) => {
    try {
      let params = { skip, limit };
      if (isActive !== null) {
        params.is_active = isActive;
      }
      const response = await api.get(`/api/clinics/${clinicId}/qa`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching QA items for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  searchClinicQAItems: async (clinicId, search, skip = 0, limit = 100, isActive = null) => {
    try {
      let params = { skip, limit, search };
      if (isActive !== null) {
        params.is_active = isActive;
      }
      const response = await api.get(`/api/clinics/${clinicId}/qa`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error searching QA items for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  getQAItemById: async (clinicId, qaId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/qa/${qaId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching QA item ${qaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  createQAItem: async (clinicId, qaItem) => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/qa`, qaItem);
      return response.data;
    } catch (error) {
      console.error(`Error creating QA item for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  importQAItems: async (clinicId, qaItems, attachRule = 'always') => {
    try {
      const response = await api.post(`/api/clinics/${clinicId}/qa/import`, qaItems, {
        params: { attach_rule: attachRule }
      });
      return response.data;
    } catch (error) {
      console.error(`Error importing QA items for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  updateQAItem: async (clinicId, qaId, qaItemData) => {
    try {
      const response = await api.put(`/api/clinics/${clinicId}/qa/${qaId}`, qaItemData);
      return response.data;
    } catch (error) {
      console.error(`Error updating QA item ${qaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  deleteQAItem: async (clinicId, qaId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/qa/${qaId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting QA item ${qaId} for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get doctor schedules
  getDoctorSchedules: async (clinicId, doctorId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/doctors/${doctorId}/schedules`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching schedules for doctor ${doctorId} in clinic ${clinicId}:`, error);
      return { schedules: [] };
    }
  },

  // Create a new doctor schedule
  createDoctorSchedule: async (clinicId, doctorId, scheduleData) => {
    try {
      const response = await api.post(
        `/api/clinics/${clinicId}/doctors/${doctorId}/schedules`,
        scheduleData
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating schedule for doctor ${doctorId} in clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Update an existing doctor schedule
  updateDoctorSchedule: async (clinicId, doctorId, scheduleData) => {
    try {
      if (!scheduleData.id) {
        throw new Error('Schedule ID is required for updates');
      }

      const response = await api.put(
        `/api/clinics/${clinicId}/doctors/${doctorId}/schedules/${scheduleData.id}`,
        scheduleData
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating schedule for doctor ${doctorId} in clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Delete doctor schedule
  deleteDoctorSchedule: async (clinicId, doctorId, scheduleId) => {
    try {
      const response = await api.delete(`/api/clinics/${clinicId}/doctors/${doctorId}/schedules/${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting schedule ${scheduleId} for doctor ${doctorId} in clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Export clinic configuration to JSON
  exportClinicConfiguration: async (clinicId) => {
    try {
      const response = await api.get(`/api/clinics/${clinicId}/export`);
      return response.data;
    } catch (error) {
      console.error(`Error exporting clinic ${clinicId} configuration:`, error);
      throw error;
    }
  },

  // Import clinic configuration from JSON to create a new clinic
  importClinicConfiguration: async (importData) => {
    try {
      const response = await api.post(`/api/clinics/import`, importData);
      return response.data;
    } catch (error) {
      console.error(`Error importing clinic configuration:`, error);
      throw error;
    }
  },
};

export const conversationService = {
  // Send a message in a conversation
  sendMessage: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_message`, {
        message_body: message
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending message in conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Send a message via outbound handler
  sendOutboundMessage: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_outbound_message`, {
        message_body: message
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending outbound message in conversation ${conversationId}:`, error);
      throw error;
    }
  },

  sendTemplate: async (conversationId, templateId) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_template`, {
        template_name: templateId
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending template in conversation ${conversationId}:`, error);
      throw error;
    }
  }
};

export const templateService = {
  // Get available templates
  getTemplates: async () => {
    return {
      templates: [
        {id: 'hello_world', content: 'Welcome and congratulations!! This message demonstrates your ability to send a WhatsApp message notification from the Cloud API, hosted by Meta. Thank you for taking the time to test with us.' },
        {id: 'Appointment Confirmation', content: 'Your appointment has been confirmed for {date} at {time}.' },
        {id: 'Appointment Reminder', content: 'Reminder: You have an appointment scheduled for {date} at {time}.' },
        {id: 'General Information', content: 'Thank you for contacting us. Our office hours are Monday-Friday, 9am-5pm.' },
        {id: 'Follow-up', content: 'Following up on your recent visit. How are you feeling?' }
      ]
    };
  },
};

// API functions for testing
export const testingService = {
  // Test agent API
  testAgentPrediction: async (requestData) => {
    try {
      const response = await api.post('/api/testing/agent/prediction', requestData);
      return response.data;
    } catch (error) {
      console.error('Error testing agent prediction:', error);
      // Return a structured error response to maintain consistent format
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to test agent prediction'
      };
    }
  },

  // Save test conversation
  saveConversation: async (clinicId, sessionId, name, chatHistory) => {
    try {
      const response = await api.post('/api/testing/conversations/save', {
        clinic_id: clinicId,
        session_id: sessionId,
        name,
        chat_history: chatHistory
      });
      return response.data;
    } catch (error) {
      console.error('Error saving test conversation:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to save conversation'
      };
    }
  },

  // Get saved conversations
  getSavedConversations: async (clinicId) => {
    try {
      const response = await api.get(`/api/testing/conversations?clinic_id=${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting saved conversations:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to get saved conversations'
      };
    }
  },
};

// API functions for analytics
export const analyticsService = {
  // Get analytics events summary
  getEventsSummary: async (days = 7, clinicId = null) => {
    try {
      const params = { days };
      if (clinicId) {
        params.reference_id = clinicId;
      }

      const response = await api.get('/api/analytics/events/summary', {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      throw error;
    }
  },

  // Get events by type
  getEventsByType: async (eventType, clinicId = null, limit = 100) => {
    try {
      const params = { limit };
      if (clinicId) {
        params.reference_id = clinicId;
      }

      const response = await api.get(`/api/analytics/events/by-type/${eventType}`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching events of type ${eventType}:`, error);
      throw error;
    }
  }
};

// API functions for Meta WhatsApp Phone Number
export const metaWhatsappPhoneNumberService = {
  // Get all phone numbers
  getAllPhoneNumbers: async () => {
    try {
      const response = await api.get('/api/settings/meta-whatsapp-phone-numbers');
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta WhatsApp phone numbers:', error);
      throw error;
    }
  },

  // Get phone number by ID
  getPhoneNumberById: async (id) => {
    try {
      const response = await api.get(`/api/settings/meta-whatsapp-phone-numbers/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching Meta WhatsApp phone number ${id}:`, error);
      throw error;
    }
  },

  // Create new phone number
  createPhoneNumber: async (phoneNumberData) => {
    try {
      const response = await api.post('/api/settings/meta-whatsapp-phone-numbers', phoneNumberData);
      return response.data;
    } catch (error) {
      console.error('Error creating Meta WhatsApp phone number:', error);
      throw error;
    }
  },

  // Update phone number
  updatePhoneNumber: async (id, phoneNumberData) => {
    try {
      const response = await api.put(`/api/settings/meta-whatsapp-phone-numbers/${id}`, phoneNumberData);
      return response.data;
    } catch (error) {
      console.error(`Error updating Meta WhatsApp phone number ${id}:`, error);
      throw error;
    }
  },

  // Delete phone number
  deletePhoneNumber: async (id) => {
    try {
      const response = await api.delete(`/api/settings/meta-whatsapp-phone-numbers/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting Meta WhatsApp phone number ${id}:`, error);
      throw error;
    }
  }
};

// API functions for Nexi Global Template
export const nexiGlobalTemplateService = {
  // Get all templates with optional type filter
  getAllTemplates: async (type = null) => {
    try {
      const response = await api.get('/api/settings/nexi-global-templates', {
        params: type ? { template_type: type } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching Nexi global templates:', error);
      throw error;
    }
  },

  // Get template by ID
  getTemplateById: async (id) => {
    try {
      const response = await api.get(`/api/settings/nexi-global-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching Nexi global template ${id}:`, error);
      throw error;
    }
  },

  // Create new template
  createTemplate: async (templateData) => {
    try {
      const response = await api.post('/api/settings/nexi-global-templates', templateData);
      return response.data;
    } catch (error) {
      console.error('Error creating Nexi global template:', error);
      throw error;
    }
  },

  // Update template
  updateTemplate: async (id, templateData) => {
    try {
      const response = await api.put(`/api/settings/nexi-global-templates/${id}`, templateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating Nexi global template ${id}:`, error);
      throw error;
    }
  },

  // Delete template
  deleteTemplate: async (id) => {
    try {
      const response = await api.delete(`/api/settings/nexi-global-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting Nexi global template ${id}:`, error);
      throw error;
    }
  },

  // Render template
  renderTemplate: async (id, languageCode, variables = null) => {
    try {
      const response = await api.get(`/api/settings/nexi-global-templates/${id}/render`, {
        params: {
          language_code: languageCode,
          ...(variables && { variables })
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error rendering Nexi global template ${id}:`, error);
      throw error;
    }
  }
};

export default api;
